import { useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import { InfoListRoot } from '@/components/Approval/@components/InfoList';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import type { SalesBrand } from '@/utils/approval/salesBrandPermission';
import type { VehicleCostEmployee } from '@/utils/approval/vehicleCost';
import {
  SalesBrandActionTag,
  SalesBrandLabel,
  SalesBrandList,
  SalesBrandPermissionRoot,
} from './styles';

export {
  SalesBrandActionTag,
  SalesBrandLabel,
  SalesBrandList,
  SalesBrandPermissionRoot,
} from './styles';

// 결재 회수(Withdraw)와 뜻이 달라 권한 회수는 context 키(회수_permission)로 따로 번역
const actionLabel = (added: boolean, t: TFunction) =>
  added ? t('추가') : t('회수', { context: 'permission' });

type ItemProps = {
  brand: SalesBrand;
  checked: boolean;
  onChange?: (checked: boolean) => void;
};

/** 브랜드 한 줄 — 보유 상태와 다르게 체크하면 추가·회수 꼬리표 */
export const SalesBrandItem = ({ brand, checked, onChange }: ItemProps) => {
  const { t } = useTranslation();
  const changed = checked !== brand.granted;
  return (
    <li>
      <SalesBrandLabel>
        <Checkbox
          size="small"
          checked={checked}
          onChange={(e) => onChange?.(e.target.checked)}
        />
        {brand.name}
      </SalesBrandLabel>
      {changed && (
        <SalesBrandActionTag added={checked}>
          {actionLabel(checked, t)}
        </SalesBrandActionTag>
      )}
    </li>
  );
};

type MarkupProps = {
  employee: VehicleCostEmployee;
  brands: SalesBrand[];
  checked: string[];
  t: TFunction;
};

/** 레거시 #sales_brand_permission_content 안쪽 마크업(class·data 속성 동일) */
const SalesBrandContentMarkup = ({
  employee,
  brands,
  checked,
  t,
}: MarkupProps) => (
  <>
    <div className="requester-info">
      <strong className="font-size-16">{t('신청자 정보')}</strong>
      <div className="textarea mt-10">
        <ul className="list-bagic-line">
          <li>
            <div className="tit">{t('이름')}</div>
            <div id="emp_name">{employee.name}</div>
          </li>
          <li>
            <div className="tit">{t('사번')}</div>
            <div>
              <span id="emp_no" data-value={employee.employeeNumber}>
                {employee.employeeNumber}
              </span>
            </div>
          </li>
          <li>
            <div className="tit">{t('소속')}</div>
            <div>{employee.affiliation}</div>
          </li>
          <li>
            <div className="tit">{t('입사일')}</div>
            <div>{employee.enterDate}</div>
          </li>
        </ul>
      </div>
    </div>
    <div className="brand-info mt-40">
      <strong className="font-size-16">{t('Sales 브랜드 조회 권한')}</strong>
      <ul className="list-brand-chkbox mt-10">
        {brands.map((brand) => {
          const isChecked = checked.includes(brand.code);
          return (
            <li key={brand.code}>
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  name="brand_permissions"
                  value={brand.code}
                  defaultChecked={isChecked}
                  data-original-state={String(brand.granted)}
                />
                <span />
                <div className="brand-name">{brand.name}</div>
              </label>
              {isChecked !== brand.granted && (
                <div
                  className={`action-label ${
                    isChecked ? 'add' : 'remove'
                  } ml-10`}
                >
                  {actionLabel(isChecked, t)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  </>
);

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildSalesBrandPermissionContentHtml = (
  props: MarkupProps,
): string => renderToStaticMarkup(<SalesBrandContentMarkup {...props} />);

const DROP_TAGS = new Set(['style', 'script', 'link']);

const isCheckedItem = (li: Element) =>
  !!li.querySelector('input[type=checkbox]')?.hasAttribute('checked');

/** 저장 content(레거시 문서 포함) 그대로 — 체크박스는 모두 비활성, 체크 안 된 줄은 흐리게 */
export const SalesBrandPermissionSnapshot = ({
  content,
}: {
  content: string;
}) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      const key = props.key as string;
      const classList = element.classList;
      if (
        tagName === 'strong' &&
        (element.parentElement?.classList.contains('requester-info') ||
          element.parentElement?.classList.contains('brand-info'))
      ) {
        return (
          <InfoSectionTitle key={key} sx={{ mb: 1 }}>
            {children}
          </InfoSectionTitle>
        );
      }
      if (classList.contains('list-bagic-line')) {
        return (
          <InfoListRoot key={key} className="list-bagic-line">
            {children}
          </InfoListRoot>
        );
      }
      if (classList.contains('list-brand-chkbox')) {
        return <SalesBrandList key={key}>{children}</SalesBrandList>;
      }
      if (
        tagName === 'li' &&
        element.parentElement?.classList.contains('list-brand-chkbox')
      ) {
        return (
          <li key={key} className={isCheckedItem(element) ? '' : 'unchecked'}>
            {children}
          </li>
        );
      }
      if (classList.contains('checkbox-inline')) {
        return (
          <SalesBrandLabel key={key} as="span" aria-disabled="true">
            {children}
          </SalesBrandLabel>
        );
      }
      if (tagName === 'input' && element.getAttribute('type') === 'checkbox') {
        return (
          <Checkbox
            key={key}
            size="small"
            disabled
            checked={element.hasAttribute('checked')}
          />
        );
      }
      if (tagName === 'span' && element.parentElement?.matches('label')) {
        return null;
      }
      if (classList.contains('action-label')) {
        return (
          <SalesBrandActionTag key={key} added={classList.contains('add')}>
            {children}
          </SalesBrandActionTag>
        );
      }
      return undefined;
    },
    [],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <SalesBrandPermissionRoot>{rendered}</SalesBrandPermissionRoot>;
};
